/**
 * `autocomplete` for the questions a form asks (M2/C3).
 *
 * A respondent on a phone should not retype their own name, email, phone and
 * address into every form they are sent; the browser will fill them in, but
 * only for a control that says what it holds. The composite fields (name,
 * address) know exactly which part each box is and name it directly; a plain
 * text box is guessed from its key and its label, and guessing nothing is the
 * safe answer — an absent attribute simply means "no suggestion".
 *
 * Nothing here changes what is stored or sent. It is the one part of M2 that
 * a desktop browser also acts on, because an attribute cannot be put behind a
 * media query — and a desktop respondent being offered their own saved
 * address is the same improvement.
 */
import type { FieldDefinition } from '../shared/types';

/** The address sub-fields, named the way the autofill spec names them. */
export const ADDRESS_AUTOCOMPLETE: Record<string, string> = {
  company: 'organization',
  attention: 'name',
  addressLine1: 'address-line1',
  addressLine2: 'address-line2',
  city: 'address-level2',
  state: 'address-level1',
  postalCode: 'postal-code',
  country: 'country-name',
  phone: 'tel',
};

export const NAME_AUTOCOMPLETE: Record<string, string> = {
  firstName: 'given-name',
  lastName: 'family-name',
};

/**
 * key + label as one lowercase haystack of WORDS. Keys here are camelCase
 * ('companyName', 'postalCode'), so the humps are split first — otherwise
 * every pattern below would need a second spelling with no boundary in it.
 */
const words = (def: FieldDefinition): string =>
  `${def.key || ''} ${def.label || ''}`
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[_\-.]+/g, ' ');

/**
 * Most specific first: "first name" must not be read as "name", and an
 * "email" question is an email whatever its key says.
 */
const GUESSES: [RegExp, string][] = [
  [/\b(first|given)\s?names?\b/, 'given-name'],
  [/\b(last|family)\s?names?\b|\bsurname\b/, 'family-name'],
  [/\b(full|your|contact|customer)\s?name\b|^\s*name\b/, 'name'],
  [/\bjob\s?title\b|\bposition\b/, 'organization-title'],
  [/\b(company|organisation|organization|business)\b/, 'organization'],
  [/\b(zip|postal)\s?code\b|\bpostcode\b/, 'postal-code'],
  [/\b(city|town|suburb)\b/, 'address-level2'],
  [/\b(state|province|county|region)\b/, 'address-level1'],
  [/\bcountry\b/, 'country-name'],
  [/\baddress\s?(line\s?)?2\b|\bapt\b|\bsuite\b|\bunit\b/, 'address-line2'],
  [/\b(street|address)\b/, 'address-line1'],
  [/\b(mobile|cell|phone|telephone)\b/, 'tel'],
  [/\be-?mail\b/, 'email'],
  [/\b(website|url|homepage)\b/, 'url'],
  [/\bbirth\s?day\b|\bdate of birth\b|\bdob\b/, 'bday'],
];

/**
 * What this field holds, or undefined when nothing here is sure enough. The
 * input type decides on its own where it can (an email box is an email); a
 * text box is read from its wording.
 */
export function autoCompleteFor(def: FieldDefinition): string | undefined {
  if (def.input_type === 'email') return 'email';
  if (def.input_type === 'tel') return 'tel';
  if (def.input_type === 'url') return 'url';
  if (def.input_type !== 'text') return undefined;
  const hay = words(def);
  for (const [re, token] of GUESSES) if (re.test(hay)) return token;
  return undefined;
}

/**
 * The single-line text-like controls, where Enter should offer to move on
 * rather than to submit. A textarea is left alone: Enter there is a newline.
 */
const HINTED = ['text', 'email', 'tel', 'url', 'number', 'search'];
export const enterKeyHintFor = (inputType: string): 'next' | undefined =>
  (HINTED.includes(inputType) ? 'next' : undefined);
